/**
 * h5/src/composables/useKioskWs.ts
 * KioskWsClient Vue 集成 composable（单例）
 *
 * 关键：网络配置变更会 reset 单例。所有 UI / 订阅必须读模块级共享状态，
 * 不能在 setup 里缓存某次 getClient() 的实例引用。
 */

import { computed, onMounted, onUnmounted, ref, watch, shallowRef } from 'vue'
import type { ComputedRef, Ref, ShallowRef } from 'vue'
import { KioskWsClient } from '@/ws/KioskWsClient'
import { createEventBus } from '@/ws/eventBus'
import { getDefaultProvider } from '@/ws/secretProvider'
import { getWsBase } from '@/utils/env'
import { logger } from '@/utils/logger'
import type { WsState, EventBus, SecretProvider, AnyEventType, EventHandler, Envelope } from '@/ws/types'

export type { WsState }

// ─── 单例 + 共享响应式状态 ────────────────────────────────────────────────────

let _client: KioskWsClient | null = null
let _bus: EventBus | null = null
let _boundWsUrl: string | null = null
let _stopStateWatch: (() => void) | null = null
let _offEnvelopeBridge: (() => void) | null = null

/** 当前生效的 WS 状态（reset 后仍可供 UI watch） */
const wsUiState: Ref<WsState> = ref('IDLE')

/** 当前客户端（shallow，供高级用途） */
const clientRef: ShallowRef<KioskWsClient | null> = shallowRef(null)

/** 当前生效的 WS URL（随 getClient / reset 更新） */
const activeWsUrl: Ref<string> = ref(getWsBase())

type EnvelopeListener = (envelope: Envelope) => void
const envelopeListeners = new Set<EnvelopeListener>()

function syncClientBindings(client: KioskWsClient): void {
  _stopStateWatch?.()
  _stopStateWatch = watch(
    client.state,
    (s) => {
      wsUiState.value = s
    },
    { immediate: true },
  )

  _offEnvelopeBridge?.()
  _offEnvelopeBridge = client.on('*', (envelope: Envelope) => {
    for (const listener of [...envelopeListeners]) {
      try {
        listener(envelope)
      } catch (err) {
        logger.warn('[useKioskWs] envelope listener 异常', { error: String(err) })
      }
    }
  })
}

/**
 * 订阅 WS 业务信封（跨 reset 仍有效；内部转到当前 client）
 */
export function onKioskWsEnvelope(handler: EnvelopeListener): () => void {
  envelopeListeners.add(handler)
  return () => {
    envelopeListeners.delete(handler)
  }
}

/**
 * 重置 WS 单例（网络配置变更后调用）
 */
export function resetKioskWsClient(): void {
  _stopStateWatch?.()
  _stopStateWatch = null
  _offEnvelopeBridge?.()
  _offEnvelopeBridge = null

  if (_client) {
    _client.destroy()
    _client = null
  }
  _bus = null
  _boundWsUrl = null
  clientRef.value = null
  wsUiState.value = 'IDLE'
  activeWsUrl.value = getWsBase()
  logger.info('[useKioskWs] 单例已重置', { ws_base: activeWsUrl.value })
}

/**
 * 获取或创建 KioskWsClient 单例
 */
function getClient(): KioskWsClient {
  const url = getWsBase()
  if (_client && _boundWsUrl !== url) {
    resetKioskWsClient()
  }
  if (_client) return _client

  _bus = createEventBus()

  let provider: SecretProvider
  try {
    provider = getDefaultProvider()
  } catch (err) {
    logger.error('[useKioskWs] SecretProvider 初始化失败', { error: String(err) })
    throw err
  }

  _boundWsUrl = url
  activeWsUrl.value = url
  _client = new KioskWsClient({
    url,
    secretProvider: provider,
    bus: _bus,
    appVersion: (import.meta.env.VITE_APP_VERSION as string | undefined) ?? '0.1.0',
  })
  clientRef.value = _client
  syncClientBindings(_client)
  logger.info('[useKioskWs] 单例已创建', { ws_base: url })

  return _client
}

/**
 * 确保单例存在并发起连接（保存网络配置 / 强制重连用）
 */
export function reconnectKioskWs(): void {
  resetKioskWsClient()
  getClient().connect()
}

/**
 * 重置单例（测试用）
 * @internal
 */
export function _resetClientForTest(): void {
  resetKioskWsClient()
}

/** 供外部直接 watch 的共享状态 */
export function getKioskWsUiState(): Ref<WsState> {
  return wsUiState
}

export function getActiveWsUrl(): Ref<string> {
  return activeWsUrl
}

// ─── Composable ──────────────────────────────────────────────────────────────

export interface UseKioskWsReturn {
  state: ComputedRef<WsState>
  isConnected: ComputedRef<boolean>
  isOnline: ComputedRef<boolean>
  /** 当前生效 WS URL（响应式） */
  wsUrl: ComputedRef<string>
  connect: () => void
  disconnect: (reason?: string) => void
  on: <T = unknown>(type: AnyEventType | '*', handler: EventHandler<T> | ((e: Envelope) => void)) => () => void
  /** 注意：可能为 null（刚 reset）；优先用 connect/disconnect */
  client: KioskWsClient | null
}

export function useKioskWs(opts?: {
  autoConnect?: boolean
  autoDisconnect?: boolean
}): UseKioskWsReturn {
  const autoConnect = opts?.autoConnect ?? true
  const autoDisconnect = opts?.autoDisconnect ?? false

  // 确保至少有一个实例（便于立刻订阅）；真正 connect 仍按 autoConnect
  getClient()

  const state = computed(() => wsUiState.value)
  const isConnected = computed(() => wsUiState.value === 'CONNECTED')
  const isOnline = computed(
    () => wsUiState.value === 'CONNECTED' || wsUiState.value === 'AUTHENTICATING',
  )
  const wsUrl = computed(() => activeWsUrl.value)

  onMounted(() => {
    if (autoConnect) {
      getClient().connect()
    }
  })

  onUnmounted(() => {
    if (autoDisconnect) {
      _client?.disconnect('component_unmounted')
    }
  })

  return {
    state,
    isConnected,
    isOnline,
    wsUrl,
    connect: () => getClient().connect(),
    disconnect: (reason?: string) => {
      if (_client) _client.disconnect(reason)
      else wsUiState.value = 'IDLE'
    },
    on: <T = unknown>(
      type: AnyEventType | '*',
      handler: EventHandler<T> | ((e: Envelope) => void),
    ) => {
      if (type === '*') {
        return onKioskWsEnvelope(handler as EnvelopeListener)
      }
      return getClient().on(type, handler as EventHandler)
    },
    client: _client,
  }
}

export { KioskWsClient }
